import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  shareReplay,
  Subject,
  Subscription,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs';
import { Organisations } from 'server/shared/collections/organisations';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Organisation } from 'server/shared/models/organisation';
import { RoleEnum } from 'server/shared/models/role.enum';
import { PoolerUser } from 'server/shared/models/user';
import { OrganisationService } from 'src/app/services/organisation.service';
import { UserService } from 'src/app/services/user.service';
import { queryParam$ } from 'src/helpers/queryParam$';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { stringIncludes } from '../../../helpers/stringIncludes';
import { CustomerService } from '../../services/customer.service';
import { SubscriptionService } from '../subscription.service';

export type DepartmentMultiSelectVariant = 'chips' | 'select';

@Component({
  selector: 'app-department-select',
  templateUrl: './department-select.component.html',
  styleUrls: ['./department-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentSelectComponent implements OnInit, OnDestroy {
  @ViewChild('multiDepartmentInput', { static: false })
  multiDepartmentInput?: NgControl;
  @ViewChild('multiDepartmentInputElement', { static: false })
  multiDepartmentInputElement?: ElementRef;
  @ViewChild('autoDepartments', { static: false })
  autoDepartments?: MatAutocomplete;
  @ViewChild('departmentInputElement', { static: false })
  departmentInputElement?: ElementRef;
  searchTerm$ = new BehaviorSubject<string | null>(null);

  // Initialize destroyed$ first to avoid initialization order issues
  destroyed$ = new Subject<void>();

  // Input properties with BehaviorSubject pattern
  disabled$ = new BehaviorSubject<boolean>(false);
  @Input() set disabled(value: boolean) {
    this.disabled$.next(value);
  }
  get disabled() {
    return this.disabled$.value;
  }

  valid$ = new BehaviorSubject<boolean>(true);
  @Input() set valid(value: boolean) {
    this.valid$.next(value);
  }
  get valid() {
    return this.valid$.value;
  }

  required$ = new BehaviorSubject<boolean>(false);
  @Input() set required(value: boolean) {
    this.required$.next(value);
  }
  get required() {
    return this.required$.value;
  }

  useQueryParam$ = new BehaviorSubject<string | false>(false);
  @Input() set useQueryParam(value: string | false) {
    this.useQueryParam$.next(value);
  }
  get useQueryParam() {
    return this.useQueryParam$.value;
  }

  autoSelectOnlyDepartment$ = new BehaviorSubject<boolean>(false);
  @Input() set autoSelectOnlyDepartment(value: boolean) {
    this.autoSelectOnlyDepartment$.next(value);
  }
  get autoSelectOnlyDepartment() {
    return this.autoSelectOnlyDepartment$.value;
  }

  autoSelectFirstDepartment$ = new BehaviorSubject<boolean>(false);
  @Input() set autoSelectFirstDepartment(value: boolean) {
    this.autoSelectFirstDepartment$.next(value);
  }
  get autoSelectFirstDepartment() {
    return this.autoSelectFirstDepartment$.value;
  }

  inviteeUserId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set InviteeUserId(value: string | undefined) {
    this.inviteeUserId$.next(value);
  }
  get InviteeUserId() {
    return this.inviteeUserId$.value;
  }

  isMultiSelect$ = new BehaviorSubject<boolean>(false);
  @Input() set isMultiSelect(value: boolean) {
    this.isMultiSelect$.next(value);
  }
  get isMultiSelect() {
    return this.isMultiSelect$.value;
  }

  multiSelectVariant$ = new BehaviorSubject<DepartmentMultiSelectVariant>(
    'chips',
  );
  @Input() set multiSelectVariant(value: DepartmentMultiSelectVariant) {
    this.multiSelectVariant$.next(value);
  }
  get multiSelectVariant() {
    return this.multiSelectVariant$.value;
  }

  lightFormField$ = new BehaviorSubject<boolean>(false);
  @Input() set lightFormField(value: boolean) {
    this.lightFormField$.next(value);
  }
  get lightFormField() {
    return this.lightFormField$.value;
  }

  multiSelectSelectedDepartments: (string | 'ALL')[] = ['ALL'];
  readonly organisationMatSelectOptionPrefix = '__org__:';

  excludedDepartmentIds$ = new BehaviorSubject<string[]>([]);
  @Input() set excludedDepartmentIds(value: string[]) {
    this.excludedDepartmentIds$.next(value);
  }
  get excludedDepartmentIds() {
    return this.excludedDepartmentIds$.value;
  }

  label$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set label(value: string | undefined) {
    this.label$.next(value);
  }
  get label() {
    return this.label$.value;
  }

  selectedDepartmentId$ = new BehaviorSubject<string | undefined>(undefined);

  @Input() set selectedDepartmentId(departmentId: string | undefined) {
    if (!departmentId) {
      this.selectDepartment(undefined, false);
      return;
    }
    this.organisationsSubscriptionReady$
      .pipe(
        filter((ready) => ready),
        take(1),
        rxNgZoneRun(this.ngZone),
      )
      .subscribe(() => {
        this.selectDepartment(departmentId, false);
      });
  }

  selectedDepartmentIds$ = new BehaviorSubject<string[]>([]);
  @Input() set selectedDepartmentIds(departmentIds: string[]) {
    this.organisationsSubscriptionReady$
      .pipe(
        takeWhile((ready) => !ready, true),
        takeUntil(this.destroyed$),
        rxNgZoneRun(this.ngZone),
      )
      .subscribe((ready) => {
        if (ready) {
          this.setSelectedDepartments(departmentIds, false);
        }
      });
  }

  selectedDepartments$ = new BehaviorSubject<Department[]>([]);

  selectedDepartmentCount$ = this.selectedDepartmentIds$.pipe(
    map((ids) => ids.length),
    shareReplay(1),
  );

  @ViewChild('matSelectSearchInput', { static: false })
  matSelectSearchInput?: ElementRef<HTMLInputElement>;

  @Output() departmentSelectionChanged = new EventEmitter<
    Department | undefined
  >();

  @Output() departmentsSelectionChanged = new EventEmitter<
    Department[] | undefined
  >();

  private organisationsSubscriptionReady$ = new BehaviorSubject<boolean>(false);

  loggedInUser$ = this.userService.readyDispoOrRequester$;

  customers$ = this.customerService.customersOfUser$.pipe(shareReplay(1));

  departmentPermissions$ = this.loggedInUser$.pipe(
    map((user) =>
      user.role === RoleEnum.DISPO
        ? user.dispoPermissions?.departments
        : user.departmentAccess,
    ),
    shareReplay(1),
  );

  allOrganisations$ = this.organisationService.readyOrganisations$;

  sortedOrganisations$ = combineLatest([
    this.allOrganisations$,
    this.customers$,
    this.userService.readyUser$,
  ]).pipe(
    map(([organisations, customers, user]) => {
      if (!customers.length) {
        return organisations;
      }
      if (user.role === RoleEnum.POOL) {
        return this.getPoolerOrganisationsForCustomers(
          organisations,
          customers,
          user,
        );
      }
      // Dispo only has one customer
      return organisations
        .map((org) => ({
          ...org,
          index:
            customers[0].organisationIds?.findIndex((id) => id === org._id) ??
            -1,
        }))
        .sort((a, b) => {
          if (a.index === -1) return 1;
          if (b.index === -1) return -1;
          return a.index - b.index;
        })
        .map(({ index, ...org }) => org);
    }),
    shareReplay(1),
  );

  filteredOrganisations$ = combineLatest([
    this.sortedOrganisations$,
    this.excludedDepartmentIds$,
    this.searchTerm$,
  ]).pipe(
    map(([organisations, excludedDepartmentIds, searchTerm]) =>
      this.filterOrganisations(
        organisations,
        excludedDepartmentIds,
        searchTerm,
      ),
    ),
    shareReplay(1),
  );

  selectedOrganisations$ = combineLatest([
    this.sortedOrganisations$,
    this.selectedDepartmentIds$,
  ]).pipe(
    map(
      ([organisations, selectedDepartmentIds]) =>
        new Set(
          organisations
            .filter((org) =>
              org.departments.every((dept) =>
                selectedDepartmentIds.includes(dept.id),
              ),
            )
            .map((org) => org._id),
        ),
    ),
    shareReplay(1),
  );

  allDepartments$ = this.filteredOrganisations$.pipe(
    map((organisations) =>
      organisations.reduce((acc: Department[], org) => {
        acc.push(...org.departments);
        return acc;
      }, []),
    ),
    shareReplay(1),
  );

  // Auto-selection side effect
  private autoSelectionEffect$ = combineLatest([
    this.allDepartments$,
    this.autoSelectOnlyDepartment$,
    this.autoSelectFirstDepartment$,
    this.disabled$,
    this.selectedDepartmentId$,
  ]).pipe(
    take(1),
    filter(
      ([departments, selectOnly, selectFirst, disabled, selectedId]) =>
        !selectedId && !disabled && departments.length > 0,
    ),
    tap(([departments, selectOnly, selectFirst]) => {
      if (departments.length === 1 && selectOnly) {
        this.selectDepartment(departments[0].id, true);
      } else if (selectFirst) {
        this.selectDepartment(departments[0].id, true);
      }
    }),
    takeUntil(this.destroyed$),
  );

  private userTracker?: Tracker.Computation;
  private departmentInputSubscription?: Subscription;
  private suppressParamSync = false;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private customerService: CustomerService,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private organisationService: OrganisationService,
  ) {}

  async ngOnInit() {
    this.subscriptionService.organisations.ready$
      .pipe(take(1))
      .subscribe(() => {
        this.organisationsSubscriptionReady$.next(true);
        this.getInitialQueryParams();
      });

    // Subscribe to auto-selection effect
    this.autoSelectionEffect$.subscribe();

    // Subscribe to query param changes
    if (this.useQueryParam) {
      combineLatest([
        queryParam$(this.route, this.useQueryParam),
        this.organisationsSubscriptionReady$,
      ])
        .pipe(
          filter(([, ready]) => ready),
          takeUntil(this.destroyed$),
        )
        .subscribe(([param]) => {
          if (!this.suppressParamSync) {
            if (param) {
              this.selectDepartment(param, true, false);
            } else {
              this.selectDepartment(undefined, true, false);
            }
          }
        });
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.userTracker) {
      this.userTracker.stop();
    }
  }

  getInitialQueryParams(): void {
    let queryParams = this.route.snapshot.queryParams;
    if (this.useQueryParam && queryParams[this.useQueryParam] !== undefined) {
      this.selectDepartment(queryParams[this.useQueryParam], true, false);
    }
  }

  private getPoolerOrganisationsForCustomers(
    organisations: Organisation[],
    customers: Customer[],
    user: PoolerUser,
  ): Organisation[] {
    const assignedDepartmentIds: string[] = [];

    customers.forEach((customer) => {
      assignedDepartmentIds.push(
        ...(user.customerInformation?.[customer._id]?.departmentIds ?? []),
      );
    });

    let userOrganisations: Organisation[] = organisations.filter(
      (organisation) => {
        return organisation.departments.some((department) =>
          assignedDepartmentIds.includes(department.id),
        );
      },
    );

    userOrganisations.forEach((organisation) => {
      organisation.departments = organisation.departments.filter(
        (department) => {
          return assignedDepartmentIds.includes(department.id);
        },
      );
    });

    return userOrganisations;
  }

  private filterOrganisations(
    allOrganisations: Organisation[],
    excludedDepartmentIds: string[],
    searchTerm: string | null,
  ): Organisation[] {
    if (searchTerm == '' || searchTerm == null) {
      let organisations = [...allOrganisations];
      if (excludedDepartmentIds?.length) {
        organisations = organisations
          .map((organisation: Organisation) => {
            organisation.departments = organisation.departments.filter(
              (department) => {
                return !excludedDepartmentIds.includes(department.id);
              },
            );
            return organisation;
          })
          .filter((organisation) => organisation.departments.length);
      }
      return organisations;
    } else {
      let organisations = allOrganisations.filter((organisation) => {
        return (
          stringIncludes(organisation.name, searchTerm) ||
          organisation.departments.some((department) =>
            stringIncludes(department.name, searchTerm),
          )
        );
      });
      organisations = organisations.map((organisation: Organisation) => {
        // prevent changes by reference to original organisation
        organisation = JSON.parse(JSON.stringify(organisation));
        if (stringIncludes(organisation.name, searchTerm)) {
          return organisation;
        } else {
          organisation.departments = organisation.departments.filter(
            (department) => stringIncludes(department.name, searchTerm),
          );
        }
        return organisation;
      });

      if (excludedDepartmentIds?.length) {
        organisations = organisations
          .map((organisation: Organisation) => {
            organisation.departments = organisation.departments.filter(
              (department) => {
                return !excludedDepartmentIds.includes(department.id);
              },
            );
            return organisation;
          })
          .filter((organisation) => organisation.departments.length);
      }
      return organisations;
    }
  }

  clearDepartmentInput(event: Event) {
    event.stopPropagation();
    this.selectDepartment(undefined);
    this.searchTerm$.next('');
    this.multiDepartmentInput?.control?.setValue('');
    if (this.isMultiSelect && this.multiSelectVariant === 'chips') {
      this.multiDepartmentInputElement?.nativeElement.focus();
    } else {
      this.departmentInputElement?.nativeElement.focus();
    }
  }

  onSingleSelectSearchTermChange(searchTerm: string | null): void {
    const normalizedSearchTerm = searchTerm ?? '';
    this.searchTerm$.next(normalizedSearchTerm);
    if (!normalizedSearchTerm && this.selectedDepartmentId$.value) {
      this.selectDepartment(undefined);
    }
  }

  matInputSelect(
    event: MatAutocompleteSelectedEvent,
    autocomplete?: MatAutocompleteTrigger,
  ) {
    if (this.isMultiSelect && this.multiSelectVariant === 'chips') {
      this.selectedDepartmentsInput(event.option.value);
      setTimeout(() => {
        autocomplete?.openPanel();
      });
    } else {
      this.selectDepartment(event.option.value);
    }
  }

  onMatSelectSelectionChange(selectedDepartments: (string | 'ALL')[]) {
    const organisationIds = selectedDepartments
      .filter((id): id is string => this.isOrganisationMatSelectValue(id))
      .map((id) => this.getOrganisationIdFromMatSelectValue(id));

    if (organisationIds.length) {
      organisationIds.forEach((organisationId) =>
        this.selectOrganisation(organisationId),
      );
      return;
    }

    let nextSelection = selectedDepartments;

    if (
      (this.multiSelectSelectedDepartments.includes('ALL') ||
        this.selectedDepartmentIds$.value.length === 0) &&
      nextSelection.length > 1
    ) {
      nextSelection = nextSelection.filter((id) => id !== 'ALL');
    }

    if (nextSelection.includes('ALL')) {
      this.multiSelectSelectedDepartments = ['ALL'];
    } else {
      this.multiSelectSelectedDepartments = nextSelection.filter(
        (id) => id !== 'ALL',
      );
    }

    const departmentIds = this.multiSelectSelectedDepartments.includes('ALL')
      ? []
      : [...this.multiSelectSelectedDepartments];
    this.setSelectedDepartments(
      departmentIds.length ? departmentIds : undefined,
    );
  }

  onMatSelectOpenedChange(opened: boolean) {
    if (opened) {
      setTimeout(() => this.matSelectSearchInput?.nativeElement.focus());
    } else {
      this.searchTerm$.next('');
      this.changeDetectorRef.markForCheck();
    }
  }

  getOrganisationMatSelectValue(organisationId: string): string {
    return `${this.organisationMatSelectOptionPrefix}${organisationId}`;
  }

  isOrganisationMatSelectValue(value: string | 'ALL'): boolean {
    return (
      typeof value === 'string' &&
      value.startsWith(this.organisationMatSelectOptionPrefix)
    );
  }

  private getOrganisationIdFromMatSelectValue(value: string): string {
    return value.slice(this.organisationMatSelectOptionPrefix.length);
  }

  selectDepartment(
    departmentId?: string,
    emit = true,
    updateQueryParams = true,
  ): void {
    if (!departmentId) {
      if (emit) {
        this.departmentSelectionChanged.emit(undefined);
      }
      this.selectedDepartmentId$.next(undefined);
      this.searchTerm$.next('');
    } else {
      this.selectedDepartmentId$.next(departmentId);
      let organisation = Organisations.findOne({
        'departments.id': departmentId,
      });
      if (organisation) {
        let department = organisation.departments.find(
          (department) => department.id == departmentId,
        );
        if (emit) {
          this.departmentSelectionChanged.emit(department);
        }
        const name = organisation.name + ' - ' + department?.name;
        this.searchTerm$.next(name);
      }
    }
    if (updateQueryParams && this.useQueryParam && !this.suppressParamSync) {
      this.suppressParamSync = true;
      this.updateQueryParams({
        [this.useQueryParam]: this.selectedDepartmentId$.value ?? null,
      });
      setTimeout(() => {
        this.suppressParamSync = false;
      }, 0);
    }
  }

  selectedDepartmentsInput(departmentId?: string) {
    if (!departmentId) {
      this.setSelectedDepartments();
    } else {
      const currentIds = this.selectedDepartmentIds$.value;
      if (currentIds.includes(departmentId)) {
        this.removeDepartment(currentIds.indexOf(departmentId));
      } else {
        this.selectedDepartmentIds$.next([...currentIds, departmentId]);
      }
      this.setSelectedDepartments(this.selectedDepartmentIds$.value);
    }
  }

  removeDepartment(index: number) {
    const currentIds = [...this.selectedDepartmentIds$.value];
    currentIds.splice(index, 1);
    this.selectedDepartmentIds$.next(currentIds);
    this.setSelectedDepartments(currentIds);
  }

  selectOrganisation(organisationId: string) {
    this.filteredOrganisations$.pipe(take(1)).subscribe((organisations) => {
      const organisation = organisations.find(
        (org) => org._id === organisationId,
      );
      if (!organisation) return;

      const currentIds = [...this.selectedDepartmentIds$.value];
      let containsAll = true;
      organisation.departments.forEach((department) => {
        if (!currentIds.includes(department.id)) {
          currentIds.push(department.id);
          containsAll = false;
        }
      });
      if (containsAll) {
        organisation.departments.forEach((department) => {
          const index = currentIds.indexOf(department.id);
          currentIds.splice(index, 1);
        });
      }
      this.selectedDepartmentIds$.next(currentIds);
      this.setSelectedDepartments(currentIds);
    });
  }

  setSelectedDepartments(departmentIds?: string[], emit = true): void {
    if (!departmentIds || !departmentIds.length) {
      if (emit) {
        this.departmentsSelectionChanged.emit(undefined);
      }
      this.selectedDepartmentId$.next(undefined);
      this.selectedDepartments$.next([]);
      this.selectedDepartmentIds$.next([]);
    } else {
      this.selectedDepartmentIds$.next(departmentIds);
      let organisations = Organisations.find({
        'departments.id': { $in: departmentIds },
      }).fetch();
      let departments: Department[] = [];
      organisations.forEach((organisation) => {
        organisation.departments.forEach((department) => {
          if (departmentIds.includes(department.id))
            departments.push(department);
        });
      });
      this.selectedDepartments$.next(departments);
      if (emit) {
        this.departmentsSelectionChanged.emit(departments);
      }
    }
    this.syncSelection(departmentIds ?? []);
    if (this.multiSelectVariant === 'chips') {
      this.searchTerm$.next('');
      this.multiDepartmentInput?.control?.setValue('');
      (this.multiDepartmentInput?.control as any).nativeElement.value = '';
      this.multiDepartmentInput?.reset('');
      this.multiDepartmentInput?.control?.markAsDirty();
    }
    this.changeDetectorRef.markForCheck();
  }

  private syncSelection(departmentIds: string[]) {
    if (this.multiSelectVariant !== 'select') {
      return;
    }
    this.multiSelectSelectedDepartments = departmentIds.length
      ? departmentIds
      : ['ALL'];
  }

  private updateQueryParams(queryParams: Record<string, string | null>) {
    this.ngZone.run(() => {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        })
        .then();
    });
  }

  trackByOrganisation(_: number, organisation: Organisation) {
    return organisation._id;
  }

  trackByDepartment(_: number, department: Department) {
    return department.id;
  }
}
